// src/hooks/common/useCurrency.js
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../auth/useAuthContext';
import { DEFAULT_CURRENCY, formatMoney } from '../../utils/money';

/**
 * Single source of truth for the active currency in the dashboard.
 *
 * Priority:
 *   1) tenant settings on the authenticated user (user.tenant.settings.currency.default)
 *   2) auth user object (user.currency)
 *   3) `DEFAULT_CURRENCY` (EGP)
 *
 * Returns:
 *   currency : string  - active ISO currency code
 *   locale   : string  - active i18n locale ("ar" | "en")
 *   format   : (amount) => string
 */
const useCurrency = () => {
    const { i18n } = useTranslation();
    const { user } = useAuthContext() || {};

    const currency = useMemo(() => {
        return (
            user?.tenant?.settings?.currency?.default ||
            user?.tenant?.currency ||
            user?.currency ||
            DEFAULT_CURRENCY
        );
    }, [user]);

    const locale = i18n?.language || 'ar';

    const format = useMemo(
        () => (amount, options) => formatMoney(amount, currency, locale, options),
        [currency, locale]
    );

    return { currency, locale, format };
};

export default useCurrency;
